import {
  Checkbox,
  FormControl,
  FormControlLabel,
  MenuItem,
  SelectChangeEvent,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import {
  ChangeEvent,
  FormEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  // useCrossCheckVerificationMutation,
  useGetStatesQuery,
  // useUpdateIdVerificationMutation,
  useUpdateMeMutation,
} from "src/api/services/patientApi";
import LoadingButton from "src/components/Buttons/LoadingButton";
import AddressVerificationModal from "./AddressVerificationModal";
import {
  formatPhoneNumber,
  formatToE164,
  isValidPhoneNumber,
} from "src/utils/formatPhoneNumber";
import { usePatient } from "src/context/PatientProvider";
import { useIdentityEvent } from "src/hooks/useIdentiyEvent";
import { useTrack } from "src/hooks/useTrack";
import { useTranslation } from "react-i18next";
import { useContinue } from "src/hooks/useContinue";
import { useProduct } from "src/context/ProductProvider";
import { Paths } from "src/constants/paths";
import PlacesAutocomplete from "./PlacesAutocomplete";
import { AddressVerified, PatientStatusType } from "src/api/types";
import { useSubmitAnswers } from "src/hooks/useSubmitAnswers";
import {
  useAddShippingAddressMutation,
  useGetShippingAddressQuery,
  useUpdateShippingAddressMutation,
  useVerifyAddressMutation,
} from "src/api/services/addressApi";

interface FormAddress {
  address_line1: string;
  address_line2: string;
  city: string;
  state: string;
  zip_code: string;
  first_name: string;
  last_name: string;
  phone_number: string;
}

interface UpdatePatient {
  status?: PatientStatusType;
  profile: {
    first_name: string;
    last_name: string;
    phone_number: string;
    region: string;
  };
}

const ShippingAddressForm = () => {
  const productSlug = useProduct((p) => p.slug);
  const navigate = useContinue();
  const { submitAnswer } = useSubmitAnswers();
  const firstName = usePatient((patient) => patient.profile.first_name ?? "");
  const lastName = usePatient((patient) => patient.profile.last_name ?? "");
  const phoneNumber = usePatient(
    (patient) => patient.profile.phone_number ?? ""
  );
  const promotionOpt = usePatient((patient) => patient.promotions_opt);
  const profileId = usePatient((patient) => patient.profile_id);
  const email = usePatient((patient) => patient.profile.email);
  const status = usePatient((patient) => patient.status);
  const [addShippingAddress] = useAddShippingAddressMutation();
  const [updateShippingAddress] = useUpdateShippingAddressMutation();
  const [updatePatient] = useUpdateMeMutation();
  // const [updateIdVerification] = useUpdateIdVerificationMutation();
  const [verifyAddress] = useVerifyAddressMutation();
  const { data: shippingAddress } = useGetShippingAddressQuery();
  const { data: states = [] } = useGetStatesQuery();
  const sendIdentifyEvent = useIdentityEvent();
  // const [crossCheck] = useCrossCheckVerificationMutation();
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState<FormAddress>({
    address_line1: "",
    address_line2: "",
    city: "",
    state: "",
    zip_code: "",
    first_name: firstName,
    last_name: lastName,
    phone_number: phoneNumber,
  });
  const [suggestedAddress, setSuggestedAddress] = useState({
    address_line1: "",
    address_line2: "",
    city: "",
    state: "",
    zip_code: "",
  });
  const [showSuggestedAddress, setShowSuggestedAddress] = useState(false);
  const [selectedOption, setSelectedOption] = useState("suggested");
  const track = useTrack();
  const page = "shipping-information";
  const { t } = useTranslation();

  const addressError = useMemo(() => t(`${page}.error`), [t]);
  const invalidAddressError = useMemo(
    () => t(`${page}.invalidAddressError`),
    [t]
  );
  const phoneError = useMemo(() => t(`${page}.phoneError`), [t]);

  const handleOnChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    setFormData((prevState) => ({
      ...prevState,
      [name!]: value,
    }));
  }, []);

  const handleSelect = useCallback((e: SelectChangeEvent) => {
    const { name, value } = e.target;

    setFormData((prevState) => ({
      ...prevState,
      [name!]: value,
    }));
  }, []);

  // const handleCrossCheckVerification = useCallback(async () => {
  //   const crossCheckValidation = await crossCheck({
  //     firstName: formData.first_name,
  //     lastName: formData.last_name,
  //     email,
  //     phone: formData.phone_number,
  //     address: {
  //       unit: formData.address_line2,
  //       streetAddress: formData.address_line1,
  //       city: formData.city,
  //       state: formData.state,
  //       postalCode: formData.zip_code,
  //       country: "US",
  //     },
  //   });

  //   const verified =
  //     crossCheckValidation.data.result.confidences.identity >= 0.8;

  //   if (verified === true) {
  //     await updatePatient({ id_verified: true }).then(() =>
  //       updateIdVerification({
  //         verification_type: "vouched",
  //         verified_at: new Date().toISOString(),
  //         status: "verified",
  //         online_visit_slug: visitSlug,
  //       })
  //     );
  //     track("Passed-Vouched", { product: productSlug });
  //   }

  //   return navigate(
  //     verified ? "payment-information" : "identity-verification-2"
  //   );
  // }, [
  //   crossCheck,
  //   email,
  //   formData.address_line1,
  //   formData.address_line2,
  //   formData.city,
  //   formData.first_name,
  //   formData.last_name,
  //   formData.phone_number,
  //   formData.state,
  //   formData.zip_code,
  //   navigate,
  //   productSlug,
  //   track,
  //   updateIdVerification,
  //   updatePatient,
  //   visitSlug,
  // ]);

  const handleAddShippingAddress = useCallback(async () => {
    const newAddress = {
      address_line1: formData.address_line1,
      address_line2: formData.address_line2,
      city: formData.city,
      state: formData.state,
      zip_code: formData.zip_code,
    };

    const address =
      selectedOption === "suggested" && showSuggestedAddress
        ? suggestedAddress
        : newAddress;

    setLoading(true);

    try {
      const payload: UpdatePatient = {
        profile: {
          first_name: formData.first_name,
          last_name: formData.last_name,
          phone_number: formatToE164(formData.phone_number),
          region: formData.state,
        },
      };

      if (status === "Lead") {
        payload.status = "Registered";
        track("lead-registered", { product: productSlug });
      }

      await updatePatient(payload).unwrap();

      if (shippingAddress) {
        await updateShippingAddress(address).unwrap();
        track("address-updated", { product: productSlug });
      } else {
        await addShippingAddress(address).unwrap();
      }

      await sendIdentifyEvent(
        {
          id: profileId,
          email: email,
          first_name: formData.first_name,
          last_name: formData.last_name,
          phone_number: formData.phone_number,
        },
        promotionOpt
      );

      if (
        states.find((state) => state.abbreviation === formData.state)
          ?.active !== true
      ) {
        navigate(`${Paths.QUESTIONS}/LtqbbZaS`);
        return;
      }

      return submitAnswer(["Ignore"]);

      // await handleCrossCheckVerification();
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
      setShowSuggestedAddress(false);
    }
  }, [
    formData.address_line1,
    formData.address_line2,
    formData.city,
    formData.state,
    formData.zip_code,
    formData.first_name,
    formData.last_name,
    formData.phone_number,
    selectedOption,
    showSuggestedAddress,
    suggestedAddress,
    shippingAddress,
    status,
    updatePatient,
    sendIdentifyEvent,
    profileId,
    email,
    promotionOpt,
    states,
    submitAnswer,
    updateShippingAddress,
    track,
    productSlug,
    addShippingAddress,
    navigate,
  ]);

  const validateAddress = useCallback(
    async (address: FormAddress) => {
      try {
        const verifiedAddress = (await verifyAddress(
          address
        ).unwrap()) as unknown as AddressVerified;
        return verifiedAddress;
      } catch {
        setError(invalidAddressError);
        setShowSuggestedAddress(false);
        return;
      }
    },
    [invalidAddressError, verifyAddress]
  );

  const handleOnSubmit = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();

      setLoading(true);

      if (
        formData.address_line1.length < 5 ||
        formData.address_line1.toLowerCase().includes("po box") ||
        formData.city.length < 4 ||
        formData.zip_code.length < 4
      ) {
        setError(addressError);
        setLoading(false);
        return;
      }

      const validatePhoneNumber = isValidPhoneNumber(
        formData.phone_number,
        "US"
      );

      if (!validatePhoneNumber) {
        setError(phoneError);
        setLoading(false);
        return;
      }

      const data = await validateAddress(formData);

      if (!data) {
        setLoading(false);
        return;
      }

      if (data.Error) {
        setError(addressError);
        setLoading(false);
      } else if (
        (data.address.streetAddress &&
          data.address.streetAddress.toUpperCase() !==
            formData.address_line1.toUpperCase().trim()) ||
        (data.address.secondaryAddress &&
          data.address.secondaryAddress.toUpperCase() !==
            formData.address_line2.toUpperCase().trim()) ||
        (data.address.state &&
          data.address.state.toUpperCase() !==
            formData.state.toUpperCase().trim()) ||
        (data.address.city &&
          data.address.city.toUpperCase() !==
            formData.city.toUpperCase().trim()) ||
        (data.address.ZIPCode && data.address.ZIPCode !== formData.zip_code)
      ) {
        setSuggestedAddress({
          address_line1: data.address.streetAddress,
          address_line2: data.address.secondaryAddress,
          city: data.address.city,
          state: data.address.state,
          zip_code: data.address.ZIPCode,
        });
        setShowSuggestedAddress(true);
        setLoading(false);
        setError(null);
      } else {
        await handleAddShippingAddress();
        setError(null);
      }
    },
    [
      addressError,
      formData,
      handleAddShippingAddress,
      phoneError,
      validateAddress,
    ]
  );

  const handleOnCheckboxChange = async (isChecked: boolean) => {
    await updatePatient({ promotions_opt: isChecked });
  };

  useEffect(() => {
    if (!shippingAddress) return;

    setFormData({
      address_line1: shippingAddress.address_line1,
      address_line2: shippingAddress.address_line2 || "",
      city: shippingAddress.city,
      state: shippingAddress.state,
      zip_code: shippingAddress.zip_code,
      first_name: firstName,
      last_name: lastName,
      phone_number: phoneNumber,
    });
  }, [shippingAddress, firstName, lastName, phoneNumber]);

  return (
    <Stack gap={2} onSubmit={handleOnSubmit} component="form">
      <Stack alignItems="flex-start" gap={4} mt={2}>
        <Stack direction="row" gap={2} sx={{ width: "100%" }}>
          <FormControl fullWidth>
            <TextField
              fullWidth
              required
              label={t(`${page}.first_name.label`)}
              name="first_name"
              id="first_name"
              value={formData?.first_name}
              onChange={handleOnChange}
            />
          </FormControl>
          <FormControl fullWidth>
            <TextField
              fullWidth
              required
              label={t(`${page}.last_name.label`)}
              name="last_name"
              id="last_name"
              value={formData?.last_name}
              onChange={handleOnChange}
            />
          </FormControl>
        </Stack>
        <PlacesAutocomplete
          onSelect={setFormData}
          value={formData.address_line1}
        />
        <FormControl fullWidth>
          <TextField
            fullWidth
            label={t(`${page}.address2.label`)}
            value={formData?.address_line2}
            name="address_line2"
            id="address_line2"
            onChange={handleOnChange}
          />
        </FormControl>
        <FormControl fullWidth error={formData?.city.length < 2}>
          <TextField
            required
            label={t(`${page}.city.label`)}
            value={formData?.city}
            name="city"
            id="city"
            onChange={handleOnChange}
          />
        </FormControl>
        <Stack direction="row" gap={2} sx={{ width: "100%" }}>
          <FormControl
            variant="filled"
            error={formData?.state.length < 2}
            fullWidth
          >
            <TextField
              fullWidth
              required
              select
              label={t(`${page}.state.label`)}
              value={formData?.state}
              name="state"
              id="state"
              onChange={handleOnChange}
            >
              {states?.map((s) => (
                <MenuItem key={s.id} value={s.abbreviation!}>
                  {s.abbreviation}
                </MenuItem>
              ))}
            </TextField>
          </FormControl>
          <FormControl error={formData?.zip_code.length < 4} fullWidth>
            <TextField
              fullWidth
              required
              label={t(`${page}.zipcode.label`)}
              value={formData?.zip_code}
              name="zip_code"
              id="zip_code"
              onChange={handleOnChange}
            />
          </FormControl>
        </Stack>
        <FormControl fullWidth>
          <TextField
            required
            label={t(`${page}.phone.label`)}
            value={formatPhoneNumber(formData.phone_number)}
            name="phone_number"
            id="phone_number"
            onChange={handleOnChange}
            slotProps={{
              htmlInput: {
                maxLength: 12,
              },
            }}
          />
        </FormControl>
        {error ? (
          <Typography sx={{ color: "red" }} textAlign="center">
            {error}
          </Typography>
        ) : null}
      </Stack>
      <Stack gap={2}>
        <Typography variant="body2" color="#6C7278">
          {t(`${page}.subtext`)}
        </Typography>
        <FormControlLabel
          sx={{
            alignItems: "start",
            gap: 1,
            margin: 0,
            color: "#6C7278",
            "& .MuiFormControlLabel-label": {
              fontSize: "14px",
            },
          }}
          control={
            <Checkbox
              checked={promotionOpt}
              onChange={(e) => handleOnCheckboxChange(e.target.checked)}
              sx={{
                padding: 0,
                "&.Mui-checked": {
                  color: "#FF8175",
                },
              }}
            />
          }
          label={t(`${page}.checkbox`)}
        />
      </Stack>
      <LoadingButton loading={loading} type="submit">
        {t("button.next", "Next")}
      </LoadingButton>
      <AddressVerificationModal
        formData={formData}
        showSuggestedAddress={showSuggestedAddress}
        setShowSuggestedAddress={setShowSuggestedAddress}
        suggestedAddress={suggestedAddress}
        setSelectedOption={setSelectedOption}
        handleSelect={handleSelect}
        selectedOption={selectedOption}
        loading={loading}
        handleAddShippingAddress={handleAddShippingAddress}
      />
    </Stack>
  );
};

export default ShippingAddressForm;
